export const lifecycleStates = [
  'draft',
  'governance-review',
  'scheduled',
  'open',
  'cutoff',
  'randomness-pending',
  'drawn',
  'claims-open',
  'closed',
  'archived',
];

export const lotteryMock = {
  summary: {
    activeDraws: 3,
    simulatedPrizePools: 184500,
    ticketsPreviewed: 1248,
    governanceAlerts: 2,
    randomnessRecords: 5,
    treasuryExposure: 18.4,
  },
  gameModels: [
    {
      id: 'gm-lotofacil-inspired',
      name: 'Lotofacil-inspired Model',
      type: 'lotofacil-inspired',
      status: 'draft',
      numberSelection: 'Select 15 numbers from a 25-number range.',
      rulesPreview: 'Multi-hit validation across a fixed pool range with transparent match tiers.',
      cutoffPolicy: 'Ticket preview closes 30 minutes before randomness request simulation.',
      consecutiveRounds: { min: 1, max: 6 },
      auditNotes: ['Rules are simulated only.', 'Future NFT tickets are not minted in this MVP.'],
    },
    {
      id: 'gm-supersete-inspired',
      name: 'SuperSete-inspired Model',
      type: 'supersete-inspired',
      status: 'governance-review',
      numberSelection: 'Choose one number per column across seven segmented groups.',
      rulesPreview: 'Column-based validation with layered probability disclosure.',
      cutoffPolicy: 'Preview entries stop at lifecycle state cutoff.',
      consecutiveRounds: { min: 1, max: 6 },
      auditNotes: ['Governance reason codes are required before activation.', 'No executable entry flow exists.'],
    },
    {
      id: 'gm-axodus-custom',
      name: 'Custom Axodus Draw Model',
      type: 'axodus-custom',
      status: 'scheduled',
      numberSelection: 'Configurable selection rules for ecosystem campaigns.',
      rulesPreview: 'Designed for DAO, Academy, Marketplace, and ecosystem participation campaigns.',
      cutoffPolicy: 'Cutoff must be governance-approved per campaign.',
      consecutiveRounds: { min: 1, max: 6 },
      auditNotes: ['Model is mock-first and extensible.', 'Treasury exposure must remain capped.'],
    },
  ],
  draws: [
    {
      id: 'draw-001',
      name: 'Constitutional Participation Draw',
      slug: 'constitutional-participation-draw',
      gameType: 'lotofacil-inspired',
      chain: 'Polygon Amoy',
      lifecycleState: 'open',
      drawDate: '2026-06-18T18:00:00Z',
      cutoffDate: '2026-06-18T17:30:00Z',
      ticketPrice: 0,
      currency: 'SIMULATED AXD',
      numbersRequired: 15,
      availableNumbers: 25,
      roundsSupported: 6,
      prizePool: 84000,
      treasuryExposure: 8.4,
      vrfProvider: 'Chainlink VRF planned',
      vrfStatus: 'mock-ready',
      merkleStatus: 'preview-only',
      governanceStatus: 'under-review',
      constitutionalStanding: 'compliant',
      operationalStanding: 'warning',
      riskLevel: 'medium',
      description:
        'Mock draw for DAO participation visibility, ticket architecture preview, and governance validation.',
      createdAt: '2026-05-01T10:00:00Z',
      updatedAt: '2026-05-15T12:30:00Z',
    },
    {
      id: 'draw-002',
      name: 'Academy Proof-of-Knowledge Draw',
      slug: 'academy-proof-of-knowledge-draw',
      gameType: 'axodus-custom',
      chain: 'Base Sepolia',
      lifecycleState: 'governance-review',
      drawDate: '2026-07-02T18:00:00Z',
      cutoffDate: '2026-07-02T16:45:00Z',
      ticketPrice: 0,
      currency: 'SIMULATED CREDIT',
      numbersRequired: 7,
      availableNumbers: 35,
      roundsSupported: 3,
      prizePool: 50500,
      treasuryExposure: 5.6,
      vrfProvider: 'Chainlink VRF planned',
      vrfStatus: 'planned',
      merkleStatus: 'not-generated',
      governanceStatus: 'under-review',
      constitutionalStanding: 'under-review',
      operationalStanding: 'verified',
      riskLevel: 'low',
      description:
        'Education-aligned mock draw for learning completion and future NFT ticket compatibility.',
      createdAt: '2026-05-04T14:00:00Z',
      updatedAt: '2026-05-16T09:00:00Z',
    },
    {
      id: 'draw-003',
      name: 'Marketplace Utility Ticket Preview',
      slug: 'marketplace-utility-ticket-preview',
      gameType: 'supersete-inspired',
      chain: 'Arbitrum Sepolia',
      lifecycleState: 'randomness-pending',
      drawDate: '2026-05-28T19:00:00Z',
      cutoffDate: '2026-05-28T18:00:00Z',
      ticketPrice: 0,
      currency: 'SIMULATED UTILITY',
      numbersRequired: 7,
      availableNumbers: 70,
      roundsSupported: 6,
      prizePool: 50000,
      treasuryExposure: 4.4,
      vrfProvider: 'Chainlink VRF planned',
      vrfStatus: 'request-mocked',
      merkleStatus: 'mock-root-ready',
      governanceStatus: 'approved-for-simulation',
      constitutionalStanding: 'compliant',
      operationalStanding: 'verified',
      riskLevel: 'low',
      description:
        'Mock utility campaign showing segmented number rules, cutoff policy, and proof visualization.',
      createdAt: '2026-04-20T11:00:00Z',
      updatedAt: '2026-05-17T10:15:00Z',
    },
  ],
  tickets: [
    {
      id: 'ticket-mock-001',
      drawId: 'draw-001',
      owner: '0xMock...A11C',
      numbers: [1, 2, 5, 7, 9, 11, 13, 14, 16, 18, 20, 21, 22, 24, 25],
      rounds: [1, 2, 3],
      nftBound: 'future-compatible',
      soulbound: true,
      mintStatus: 'disabled-mock-only',
      validationStatus: 'preview-valid',
      prizeEligible: 'simulated-only',
      expirationDate: '2026-08-18T00:00:00Z',
      claimStatus: 'claim-disabled',
      burnPolicy: 'simulated burn-on-claim policy only',
      createdAt: '2026-05-12T10:30:00Z',
    },
    {
      id: 'ticket-mock-002',
      drawId: 'draw-003',
      owner: '0xMock...B22D',
      numbers: [4, 1, 8, 3, 9, 6, 2],
      rounds: [1, 2, 3, 4, 5, 6],
      nftBound: 'future-compatible',
      soulbound: false,
      mintStatus: 'disabled-mock-only',
      validationStatus: 'preview-valid',
      prizeEligible: 'simulated-only',
      expirationDate: '2026-09-01T00:00:00Z',
      claimStatus: 'claim-disabled',
      burnPolicy: 'simulated transferable ticket policy only',
      createdAt: '2026-05-14T16:20:00Z',
    },
  ],
  prizePools: [
    {
      id: 'pool-001',
      drawId: 'draw-001',
      totalAmount: 84000,
      treasuryAllocation: 47000,
      communityAllocation: 25000,
      reserveAllocation: 9000,
      operationalFee: 2000,
      governanceFee: 1000,
      payoutStatus: 'non-executable simulated accounting',
    },
    {
      id: 'pool-002',
      drawId: 'draw-002',
      totalAmount: 50500,
      treasuryAllocation: 26000,
      communityAllocation: 17000,
      reserveAllocation: 5000,
      operationalFee: 1500,
      governanceFee: 1000,
      payoutStatus: 'non-executable simulated accounting',
    },
    {
      id: 'pool-003',
      drawId: 'draw-003',
      totalAmount: 50000,
      treasuryAllocation: 28000,
      communityAllocation: 14000,
      reserveAllocation: 5000,
      operationalFee: 2000,
      governanceFee: 1000,
      payoutStatus: 'non-executable simulated accounting',
    },
  ],
  randomnessRecords: [
    {
      id: 'vrf-001',
      drawId: 'draw-003',
      provider: 'Chainlink VRF planned',
      requestId: 'mock-vrf-request-7f31',
      entropySource: 'mock seed preview',
      verificationHash: '0xmocked0f3c9b9a7d5e2a1',
      executionStatus: 'request-mocked',
      verificationStatus: 'simulated-verifiable',
      lifecycleState: 'randomness-pending',
      generatedAt: '2026-05-17T12:00:00Z',
    },
    {
      id: 'vrf-002',
      drawId: 'draw-001',
      provider: 'Chainlink VRF planned',
      requestId: 'mock-vrf-request-pending',
      entropySource: 'not requested',
      verificationHash: 'pending',
      executionStatus: 'planned',
      verificationStatus: 'not-started',
      lifecycleState: 'open',
      generatedAt: null,
    },
  ],
  winners: [
    {
      id: 'winner-sim-001',
      drawId: 'draw-003',
      ticketId: 'ticket-mock-002',
      prizeAmount: 0,
      payoutStatus: 'non-executable simulation',
      verificationStatus: 'mock-reviewed',
      claimedAt: null,
    },
  ],
  governanceValidations: [
    {
      id: 'gov-001',
      drawId: 'draw-001',
      constitutionalStanding: 'compliant',
      reasonCodes: ['TREASURY_CAP_VISIBLE', 'MOCK_ONLY_FLOW', 'VRF_PROVIDER_PLANNED'],
      severity: 'medium',
      treasuryRisk: 'capped simulated exposure',
      acsReview: 'monitoring recommended',
      operationalApprovalState: 'pending governance review',
    },
    {
      id: 'gov-002',
      drawId: 'draw-003',
      constitutionalStanding: 'compliant',
      reasonCodes: ['CUTOFF_DEFINED', 'NO_EXECUTABLE_PAYOUT', 'AUDIT_LOG_PRESENT'],
      severity: 'low',
      treasuryRisk: 'read-only simulated exposure',
      acsReview: 'no anomaly in mock telemetry',
      operationalApprovalState: 'approved for simulation',
    },
  ],
  treasuryExposure: [
    { label: 'Constitutional Participation Draw', exposure: 8.4, status: 'simulated' },
    { label: 'Academy Proof-of-Knowledge Draw', exposure: 5.6, status: 'simulated' },
    { label: 'Marketplace Utility Ticket Preview', exposure: 4.4, status: 'simulated' },
  ],
  acsReviews: [
    {
      id: 'acs-001',
      drawId: 'draw-001',
      workflow: 'Draw Validation',
      status: 'reviewing',
      finding: 'Treasury cap and cutoff policy visible; awaiting governance sign-off.',
    },
    {
      id: 'acs-002',
      drawId: 'draw-003',
      workflow: 'Randomness Audit',
      status: 'mock-reviewed',
      finding: 'Mock request id links to lifecycle state randomness-pending.',
    },
  ],
  lifecycle: lifecycleStates.map((state, index) => ({
    id: `life-${index + 1}`,
    state,
    order: index + 1,
    description: `Draw lifecycle checkpoint: ${state}.`,
    executable: false,
  })),
  auditLog: [
    {
      id: 'audit-001',
      actor: 'governance-module',
      action: 'recorded mock validation',
      target: 'draw-001',
      severity: 'info',
      timestamp: '2026-05-15T12:30:00Z',
    },
    {
      id: 'audit-002',
      actor: 'acs-monitor',
      action: 'linked mock randomness request',
      target: 'draw-003',
      severity: 'info',
      timestamp: '2026-05-17T12:00:00Z',
    },
  ],
};
